import type { ReactNode } from "react";
import { Outlet } from "react-router-dom";

import { Header } from "@/components/shared/Header";
import { AppDownloadBanner } from "@/components/shared/AppDownloadBanner";
import { ProfileBanners } from "@/components/shared/ProfileBanners";
import { Sidebar } from "@/components/shared/Sidebar";
import { TwoFactorEnrollmentGate } from "@/components/shared/TwoFactorEnrollmentGate";
import { useTwoFactorGate } from "@/hooks/useTwoFactorGate";
import { SidebarProvider } from "@/contexts/SidebarContext";

interface AppLayoutProps {
  children?: ReactNode;
}

function LayoutContent({ children }: AppLayoutProps): JSX.Element {
  const { blocking, holding } = useTwoFactorGate();

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-muted/40 dark:bg-muted">
      {/* Mounted here, not per page: a professional who owes a second factor
          must be stopped on EVERY authenticated route, and a per-page list is
          a list to forget to add to. */}
      <TwoFactorEnrollmentGate open={blocking} />
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header />
        <ProfileBanners />
        <AppDownloadBanner />
        <main className="min-h-0 flex-1 overflow-y-auto bg-muted/40 dark:bg-muted">
          {/*
            THE PAGE DOES NOT MOUNT BEHIND THE GATE, and that is not about
            looks — the overlay already hides it. A mounted page runs its own
            queries, the server refuses every one of them, and each refusal
            becomes a toast: "No se pudieron cargar las solicitudes." stacked
            over the modal that was explaining why. There are 350-odd
            toast.error sites in this app; the fix is to not make the calls,
            not to teach every catch block about 2FA.
          */}
          {holding ? null : children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}

export function AppLayout({ children }: AppLayoutProps = {}): JSX.Element {
  return (
    <SidebarProvider>
      <LayoutContent>{children}</LayoutContent>
    </SidebarProvider>
  );
}
